/**
 * Which Sales sub-tabs the signed-in user can open, from `/access`
 * section_permissions. Mirrors the core API's role gates
 * (bookings-infra role_permissions/workflow_schema.py) so the portal never
 * offers a tab whose data request the API would refuse with a 403:
 *
 * - Transactions and Payments both read `GET /business_locations/{id}/sales`,
 *   which is gated on `payments.canView`;
 * - Cash Drawer reads the drawer sessions, gated on `cashRegister.canView`;
 * - the Gift Cards list is ungated, so it only follows its feature flag.
 *
 * Fail-closed: undefined permissions open nothing. The Sales nav entry
 * (navSections) and SalesPage's tab bar + routes all build from
 * `getVisibleSalesTabs`, so the rules live in one place.
 */
import type { SectionPermissions } from "../types/featureAccess";

/** Absolute base path the Sales tab and all its sub-routes live under. */
export const SALES_BASE = "/admin/sales";

export type SalesTabPath =
  | "transactions"
  | "purchases"
  | "cash-drawer"
  | "gift-cards";

export interface SalesTab {
  label: string;
  /** Route segment beneath `SALES_BASE`. */
  path: SalesTabPath;
}

export type SalesPermissions = Partial<SectionPermissions> | null | undefined;

export interface SalesTabOptions {
  /** REACT_APP_GIFT_CARDS_ENABLED (see utils/giftCardsFlag). */
  giftCardsEnabled: boolean;
}

interface SalesTabRule extends SalesTab {
  isOpen: (perms: SalesPermissions, options: SalesTabOptions) => boolean;
}

const canViewSalesList = (perms: SalesPermissions): boolean =>
  perms?.payments?.canView === true;

/** In tab-bar order; the first open tab is where Sales lands. */
const SALES_TAB_RULES: SalesTabRule[] = [
  { label: "Transactions", path: "transactions", isOpen: canViewSalesList },
  { label: "Payments", path: "purchases", isOpen: canViewSalesList },
  {
    label: "Cash Drawer",
    path: "cash-drawer",
    isOpen: (perms) => perms?.cashRegister?.canView === true,
  },
  {
    label: "Gift Cards",
    path: "gift-cards",
    isOpen: (_perms, { giftCardsEnabled }) => giftCardsEnabled,
  },
];

/** The sub-tabs the user can open, in tab-bar order (empty = no Sales). */
export function getVisibleSalesTabs(
  perms: SalesPermissions,
  options: SalesTabOptions,
): SalesTab[] {
  return SALES_TAB_RULES.filter((rule) => rule.isOpen(perms, options)).map(
    ({ label, path }) => ({ label, path }),
  );
}

/** Absolute route for a Sales sub-tab, e.g. `/admin/sales/transactions`. */
export const salesTabRoute = (tab: SalesTab): string =>
  `${SALES_BASE}/${tab.path}`;
